import './TestOneTimePaymentForm.scss';

import { useRecurly, useCheckoutPricing } from "@recurly/react-recurly";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PricingFormState } from "types/purchaseMembership";
import { RecurlyError } from '@recurly/recurly-js';
import { TestPaymentDetails } from './TestPaymentDetails';

export const TestOneTimePaymentForm = observer(() => {
  const { t } = useTranslation();
  const store = useStores().testOneTimePaymentStore;
  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const [is3DSecureInitialized, setIs3DSecureInitialized] = useState(false);
  const [recurlyPlanError, setRecurlyPlanError] = useState<RecurlyError | null>(null);
  const [pricingFormState, setPricingFormState] = useState<PricingFormState>({
    plan: 'tmu_us',
    coupon: '',
    billingCountry: '',
    billingPostalCode: '',
    billingVatNumber: '',
    address: {
      email: '',
      first_name: '',
      last_name: '',
      address1: '',
      address2: '',
      city: '',
      country: '',
      state: '',
      postal_code: ''
    }
  });
  const [includeSubscription, setIncludeSubscription] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const [{ price, loading }, setCheckoutPricing] = useCheckoutPricing(pricingFormState, setRecurlyPlanError);

  useEffect(() => {
    const subscriptions = includeSubscription && pricingFormState.plan ? [{ plan: pricingFormState.plan }] : [];
    const address = {
      country: pricingFormState.billingCountry,
      postal_code: pricingFormState.billingPostalCode,
      first_name: pricingFormState.address.first_name,
      last_name: pricingFormState.address.last_name,
    };

    setCheckoutPricing({ ...pricingFormState, address, subscriptions });
  }, [pricingFormState, setCheckoutPricing, includeSubscription]);

  useEffect(() => {
    setErrorMessage(store.ThreeDSecureError);
  }, [store.ThreeDSecureError]);

  const handleThreeDSecure = useCallback(
    (cardTokenId: string, actionTokenId: string) => {
      setIs3DSecureInitialized(false);
      const threeDSecure = recurly.Risk().ThreeDSecure({ actionTokenId: actionTokenId });

      threeDSecure.on('token', (actionToken) =>
        store.sendThreeDSecureRequest(
          cardTokenId,
          pricingFormState.address.email,
          includeSubscription ? pricingFormState.plan : null,
          price.currency.code,
          pricingFormState.coupon,
          actionToken.id,
          includeSubscription,
          selectedProductIds
        )
      );

      threeDSecure.attach(document.body);
      store.isTokenHandling = false;
    },
    [store, recurly, pricingFormState, price, includeSubscription, selectedProductIds]
  );

  const onRecurlyValidationError = (err: RecurlyError) => {
    if (err.fields) {
      setErrorMessage(`${err.message}. ${t('TheFollowingFieldsAppearToBeInvalid', { ns: 'purchaseMembership' })} ${err.fields.join(', ')}`);
    } else if (err && err.message) {
      setErrorMessage(err.message);
    }
  };

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      setIs3DSecureInitialized(false);

      if (event.preventDefault) event.preventDefault();
      setErrorMessage('');
      setIsButtonEnabled(false);

      formRef.current &&
        recurly.token(formRef.current, (err, token) => {
          if (err) {
            console.log(`Error creating Recurly token: ${err.message}`);
            onRecurlyValidationError(err);
          } else {
            console.log(`Recurly token created: ${token.id}`);
            store.tokenId = token.id;
            store.tokenHandler(
              pricingFormState.address.email,
              includeSubscription ? pricingFormState.plan : null,
              price.currency.code,
              pricingFormState.coupon,
              includeSubscription,
              selectedProductIds
            );
          }
          setIsButtonEnabled(true);
        });
    },
    [store, recurly, pricingFormState, price, includeSubscription, selectedProductIds]
  );

  useEffect(() => {
    if (store.isTokenHandling && store.ThreeDSecureToken && store.tokenId && !is3DSecureInitialized) {
      setIs3DSecureInitialized(true);
      handleThreeDSecure(store.tokenId, store.ThreeDSecureToken);
    }
  }, [store.isTokenHandling, handleThreeDSecure, store.ThreeDSecureToken, store.tokenId]);

  useEffect(() => {
    store.tokenHandlingError && setErrorMessage(store.tokenHandlingError);
  }, [store.tokenHandlingError]);

  return (
    <form ref={formRef} onSubmit={(e) => handleSubmit(e)}>
      <div className='go-premium-section'>
        <strong className='section-title'>{t('PaymentDetails', { ns: 'purchaseMembership' })}</strong>
        <TestPaymentDetails
          setRecurlyPlanError={setRecurlyPlanError}
          price={price}
          pricingFormState={pricingFormState}
          setPricingFormState={setPricingFormState}
          errorMessage={errorMessage}
          loading={loading}
          recurlyPlanError={recurlyPlanError}
          isButtonEnabled={isButtonEnabled}
          includeSubscription={includeSubscription}
          setIncludeSubscription={setIncludeSubscription}
          selectedProductIds={selectedProductIds}
          setSelectedProductIds={setSelectedProductIds}
        />
      </div>
    </form>
  );
});